import { useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { api, actionKey } from '../../api';
import { Dialog, ErrorBox, Field, Loading, useBusy } from '../../ui';

interface Preview { name: string; archived: boolean; canDelete: boolean; deleteBlockedReason?: string; canRestore: boolean }
export function LifecycleActions({ path, business = false, returnTo }: { path: string; business?: boolean; returnTo?: string }) {
  const [open, setOpen] = useState(false);
  const [action, setAction] = useState<'ARCHIVE' | 'DELETE' | 'RESTORE' | null>(null);
  const [reason, setReason] = useState('');
  const [name, setName] = useState('');
  const [uncertain, setUncertain] = useState(false);
  const { busy, error, run, setError } = useBusy();
  const qc = useQueryClient();
  const navigate = useNavigate();
  const preview = useQuery({ queryKey: ['lifecycle', path], queryFn: () => api.get<Preview>(path), enabled: open });
  const close = () => { if (!busy) { setOpen(false); if (!uncertain) { setAction(null); setReason(''); setName(''); setError(null); } } };
  return <>
    <button className="btn small" onClick={() => setOpen(true)}>{business ? 'Manage business' : 'Archive / delete relationship'}</button>
    <Dialog open={open} title={business ? 'Manage business' : 'Manage relationship'} onClose={close}>
      {preview.isLoading ? <Loading /> : <ErrorBox error={preview.error} />}
      {preview.data ? <div className="stack">
        <strong>{preview.data.name}</strong>
        <p>{business ? 'Applies to this business and its customer/supplier relationships.' : 'Applies only to the relationship between your business and this customer or supplier. Their other business relationships and sign-in are unaffected.'}</p>
        <p>Archive stops new purchases and cancels unused indents. Existing balances, statements, payments and history remain available. Resolve fills already in progress first.</p>
        <p>Delete is available only for unused records. An audit record is retained.</p>
        <div className="row">
          {!preview.data.archived ? <button className="btn" disabled={busy || uncertain} onClick={() => setAction('ARCHIVE')}>Archive</button> : <span>Archived</span>}
          {preview.data.canRestore ? <button className="btn" disabled={busy || uncertain} onClick={() => setAction('RESTORE')}>Restore</button> : null}
          <button className="btn danger" disabled={busy || uncertain || !preview.data.canDelete} onClick={() => setAction('DELETE')}>Delete unused record</button>
        </div>
        {preview.data.deleteBlockedReason ? <p className="muted">{preview.data.deleteBlockedReason}</p> : null}
        {action ? <>
          <p>{action === 'RESTORE' ? 'Restoring allows new purchases again. Cancelled indents stay cancelled.' : `Confirm ${action.toLowerCase()} for ${preview.data.name}.`}</p>
          <Field label="Reason"><input value={reason} maxLength={300} disabled={busy || uncertain} onChange={e => setReason(e.target.value)} /></Field>
          <Field label="Type the business name to confirm"><input value={name} disabled={busy || uncertain} onChange={e => setName(e.target.value)} autoComplete="off" /></Field>
          {uncertain ? <p>The response was lost. Retry this same confirmation to check the saved result.</p> : null}
          <ErrorBox error={error} />
          <button className={`btn ${action === 'RESTORE' ? 'primary' : 'danger'}`} disabled={busy || reason.trim().length < 3 || name.trim() !== preview.data.name} onClick={() => void run(async () => {
            const key = actionKey(`lifecycle:${path}:${action}`);
            try {
              await api.post(path, { action, reason, confirmName: name }, { idempotencyKey: key.key });
              key.done(); setUncertain(false); setOpen(false); setAction(null); setName(''); setReason('');
              if (action === 'DELETE' && returnTo) navigate(returnTo);
              await qc.invalidateQueries();
            } catch (err) {
              const status = (err as { status?: number }).status;
              const definitive = Boolean(status && status < 500 && (err as { code?: string }).code !== 'REQUEST_IN_PROGRESS');
              if (definitive) key.done();
              setUncertain(!definitive);
              throw err;
            }
          })}>{busy ? 'Please wait…' : `Confirm ${action.toLowerCase()}`}</button>
        </> : null}
      </div> : null}
    </Dialog>
  </>;
}
